import { ActiveStateExactMatchStrategy } from "@/domains/navigation/strategies/active-state-exact-match-strategy"
import { StaticURLStrategy } from "@/domains/navigation/strategies/static-url-strategy"

// Next.js compatible Location type (matches what we create from useRouter)
export interface Location {
  pathname: string
  search: string
  hash: string
  state: any
  key: string
}

export interface SubNavigationItem {
  readonly id: string
  readonly label: string
  readonly href: string
  readonly iconName: string
  readonly isExternal: boolean
}

// Base interface that all navigation items must implement
export interface NavigationItemBase {
  readonly id: string
  readonly label: string
  readonly href: string
  readonly iconName: string
  readonly isExternal: boolean
  readonly isActive: (location: Location) => boolean
  readonly parent?: NavigationItemBase
  setActiveStrategy: (strategy: ActiveStateStrategy) => void
  setURLStrategy: (strategy: URLGenerationStrategy) => void
  setParent: (parent: NavigationItemBase) => void
  generateHref: () => string
}

// Strategy interface for determining active state
export interface ActiveStateStrategy {
  isActive(location: Location, item: NavigationItemBase): boolean
}

// Strategy interface for generating URLs
export interface URLGenerationStrategy {
  generateHref(item: NavigationItemBase): string
}

// Base class that implements the common active state logic
export abstract class BaseNavigationItem implements NavigationItemBase {
  abstract readonly id: string
  abstract readonly label: string
  abstract readonly href: string
  abstract readonly iconName: string
  abstract readonly isExternal: boolean

  // Parent relationship
  protected _parent?: NavigationItemBase

  // Strategy for determining active state
  protected activeStrategy: ActiveStateStrategy = new ActiveStateExactMatchStrategy()

  // Strategy for generating URLs
  protected urlStrategy: URLGenerationStrategy = new StaticURLStrategy()

  // Getter for parent
  get parent(): NavigationItemBase | undefined {
    return this._parent
  }

  // Method to set the parent
  setParent(parent: NavigationItemBase): void {
    this._parent = parent
  }

  // Method to set the active strategy
  setActiveStrategy(strategy: ActiveStateStrategy): void {
    this.activeStrategy = strategy
  }

  // Method to set the URL strategy
  setURLStrategy(strategy: URLGenerationStrategy): void {
    this.urlStrategy = strategy
  }

  // Method to determine if this item is active
  get isActive(): (location: Location) => boolean {
    return (location: Location) => this.activeStrategy.isActive(location, this)
  }

  // Method to generate the href based on parent hierarchy
  generateHref(): string {
    // If this item has a parent, build URL from parent + this item's path
    if (this._parent) {
      const parentUrl = this._parent.generateHref()
      const itemPath = this.href.startsWith("/") ? this.href.slice(1) : this.href
      return [parentUrl, itemPath].join("/")
    }

    // No parent - use the URL strategy directly
    return this.urlStrategy.generateHref(this)
  }
}

// Sub-navigation item class (no sub-items, but full active state support)
export abstract class NavigationSubItem extends BaseNavigationItem {
  abstract readonly id: string
  abstract readonly label: string
  abstract readonly href: string
  abstract readonly iconName: string
  abstract readonly isExternal: boolean
}

// Main navigation item class (can have sub-items)
export abstract class NavigationItem extends BaseNavigationItem {
  abstract readonly isExternal: boolean
  abstract readonly tabs: any[]
  abstract readonly subItems: NavigationSubItem[]
}
